import type { ButtonHTMLAttributes, FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

export type ButtonVariant = 'primary' | 'secondary' | 'dark' | 'whatsapp' | 'outline' | 'ghost' | 'danger'
export type ButtonSize = 'sm' | 'md' | 'lg' | 'icon' | 'icon-sm'

const VARIANTS: Record<ButtonVariant, string> = {
    primary: 'border-2 border-brand-choco bg-brand-yellow text-brand-choco shadow-pop hover:-translate-y-0.5 hover:bg-[#FFC52E] hover:shadow-[0_7px_0_0_#3B1F0E] active:translate-y-[3px] active:shadow-pop-sm',
    secondary: 'border-2 border-brand-choco bg-white text-brand-choco shadow-pop hover:-translate-y-0.5 hover:bg-brand-cream hover:shadow-[0_7px_0_0_#3B1F0E] active:translate-y-[3px] active:shadow-pop-sm',
    dark: 'border-2 border-brand-choco bg-brand-choco text-brand-cream shadow-[0_5px_0_0_#F28C1B] hover:-translate-y-0.5 hover:shadow-[0_7px_0_0_#F28C1B] active:translate-y-[3px] active:shadow-[0_2px_0_0_#F28C1B]',
    whatsapp: 'border-2 border-brand-choco bg-brand-green text-white shadow-pop hover:-translate-y-0.5 hover:bg-[#0E9447] hover:shadow-[0_7px_0_0_#3B1F0E] active:translate-y-[3px] active:shadow-pop-sm',
    outline: 'border-2 border-brand-choco/15 bg-white text-brand-choco hover:border-brand-choco/40 hover:bg-brand-cream',
    ghost: 'text-brand-choco hover:bg-brand-sand/70',
    danger: 'border-2 border-brand-choco bg-destructive text-white shadow-pop hover:-translate-y-0.5 active:translate-y-[3px] active:shadow-pop-sm'
}

const SIZES: Record<ButtonSize, string> = {
    sm: 'h-9 px-4 text-sm',
    md: 'h-11 px-5 text-[15px]',
    lg: 'h-14 px-7 text-base sm:text-lg',
    icon: 'h-11 w-11',
    'icon-sm': 'h-9 w-9'
}

export const buttonStyles = ({
    variant = 'primary',
    size = 'md',
    className
}: { variant?: ButtonVariant, size?: ButtonSize, className?: string } = {}): string => cn(
    'inline-flex shrink-0 select-none items-center justify-center gap-2 rounded-full font-heading font-semibold tracking-wide transition-all duration-150',
    'focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand-orange/40',
    'disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50',
    VARIANTS[variant],
    SIZES[size],
    className
)

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: ButtonVariant
    size?: ButtonSize
    loading?: boolean
    children?: ReactNode
}

export const Button: FC<ButtonProps> = ({
    variant,
    size,
    loading = false,
    className,
    children,
    disabled,
    type = 'button',
    ...props
}): ReactNode => {
    return (
        <button
            type={type}
            className={buttonStyles({ variant, size, className })}
            disabled={disabled || loading}
            aria-busy={loading || undefined}
            {...props}
        >
            {loading && <span className='size-4 animate-spin rounded-full border-2 border-current border-r-transparent' aria-hidden='true' />}
            {children}
        </button>
    )
}
